<template>
  <div class="ee-panel">
    <!-- Editorial Essay -->
    <div class="ee-block">
      <div class="ee-block-head">
        <span class="ee-block-title">Editorial Essay</span>
        <span class="ee-badge" :class="hasEditorialEssay ? 'is-set' : 'is-empty'">
          {{ hasEditorialEssay ? 'Set' : 'Not generated' }}
        </span>
        <span v-if="editorialEssayWordCount" class="ee-word-count">~{{ editorialEssayWordCount }} words</span>
      </div>
      <template v-if="hasEditorialEssay">
        <div class="ee-body-preview">
          <iframe :srcdoc="editorialEssayHtml" sandbox="" referrerpolicy="no-referrer"></iframe>
        </div>
        <div class="ee-actions">
          <el-button size="mini" icon="el-icon-document-copy" @click="copy(editorialEssay.body_markdown || editorialEssay.body || '', 'Editorial essay')">Copy text</el-button>
          <el-button v-if="editorialEssay.body_html" size="mini" icon="el-icon-document-copy" @click="copy(editorialEssay.body_html, 'Editorial essay HTML')">Copy HTML</el-button>
        </div>
      </template>
    </div>

    <!-- Editorial Cover HTML -->
    <div class="ee-block">
      <div class="ee-block-head">
        <span class="ee-block-title">Editorial Cover</span>
        <span class="ee-badge" :class="hasEditorialCover ? 'is-set' : 'is-empty'">
          {{ hasEditorialCover ? 'Set' : 'Not generated' }}
        </span>
      </div>
      <template v-if="hasEditorialCover">
        <div class="ee-cover-preview">
          <iframe :srcdoc="collectionForm.editorial_cover_html" sandbox="allow-same-origin" referrerpolicy="no-referrer"></iframe>
        </div>
        <div class="ee-actions">
          <el-button size="mini" icon="el-icon-document-copy" @click="copy(collectionForm.editorial_cover_html, 'Cover HTML')">Copy HTML</el-button>
        </div>
      </template>
    </div>

    <!-- Thesis Essay -->
    <div class="ee-block">
      <div class="ee-block-head">
        <span class="ee-block-title">Thesis Essay</span>
        <span class="ee-badge" :class="hasThesisEssay ? 'is-set' : 'is-empty'">
          {{ hasThesisEssay ? 'Set' : 'Not generated' }}
        </span>
        <span v-if="thesisEssayWordCount" class="ee-word-count">~{{ thesisEssayWordCount }} words</span>
      </div>
      <template v-if="hasThesisEssay">
        <div class="ee-body-preview">
          <iframe :srcdoc="thesisEssayHtml" sandbox="" referrerpolicy="no-referrer"></iframe>
        </div>
        <div class="ee-actions">
          <el-button size="mini" icon="el-icon-document-copy" @click="copy(thesisEssay.body_markdown || thesisEssay.body || '', 'Thesis essay')">Copy text</el-button>
          <el-button v-if="thesisEssay.body_html" size="mini" icon="el-icon-document-copy" @click="copy(thesisEssay.body_html, 'Thesis essay HTML')">Copy HTML</el-button>
        </div>
      </template>
    </div>

    <!-- Collection Brief -->
    <div class="ee-block">
      <div class="ee-block-head">
        <span class="ee-block-title">Collection Brief</span>
        <span class="ee-badge" :class="hasCollectionBrief ? 'is-set' : 'is-empty'">
          {{ hasCollectionBrief ? 'Set' : 'Not generated' }}
        </span>
        <span v-if="collectionBriefWordCount" class="ee-word-count">~{{ collectionBriefWordCount }} words</span>
      </div>
      <template v-if="hasCollectionBrief">
        <div class="ee-body-preview ee-body-preview-tall">
          <iframe :srcdoc="collectionBriefHtml" sandbox="" referrerpolicy="no-referrer"></iframe>
        </div>
        <div class="ee-actions">
          <el-button size="mini" icon="el-icon-document-copy" @click="copy(collectionBrief.body_markdown || collectionBrief.body || '', 'Collection brief')">Copy text</el-button>
          <el-button v-if="collectionBrief.body_html" size="mini" icon="el-icon-document-copy" @click="copy(collectionBrief.body_html, 'Collection brief HTML')">Copy HTML</el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "EditorialEssaysPanel",
  props: {
    collectionForm: { type: Object, required: true },
    collectionId: { type: String, default: "" },
  },
  computed: {
    editorialEssay() {
      return this.collectionForm.editorial_essay || null;
    },
    hasEditorialEssay() {
      const e = this.editorialEssay;
      return !!(e && (e.body_markdown || e.body_html || e.body));
    },
    editorialEssayWordCount() {
      const e = this.editorialEssay;
      if (!e) return 0;
      const text = e.body_markdown || e.body || "";
      return text ? text.split(/\s+/).filter(Boolean).length : 0;
    },
    editorialEssayHtml() {
      const e = this.editorialEssay;
      if (!e) return "";
      if (e.body_html) return this.wrapHtml(e.body_html);
      if (e.body_markdown || e.body) return this.wrapHtml("<pre style='white-space:pre-wrap;font-family:inherit;'>" + this.esc(e.body_markdown || e.body) + "</pre>");
      return "";
    },

    thesisEssay() {
      return this.collectionForm.thesis_essay || null;
    },
    hasThesisEssay() {
      const e = this.thesisEssay;
      return !!(e && (e.body_markdown || e.body_html || e.body));
    },
    thesisEssayWordCount() {
      const e = this.thesisEssay;
      if (!e) return 0;
      const text = e.body_markdown || e.body || "";
      return text ? text.split(/\s+/).filter(Boolean).length : 0;
    },
    thesisEssayHtml() {
      const e = this.thesisEssay;
      if (!e) return "";
      if (e.body_html) return this.wrapHtml(e.body_html);
      if (e.body_markdown || e.body) return this.wrapHtml("<pre style='white-space:pre-wrap;font-family:inherit;'>" + this.esc(e.body_markdown || e.body) + "</pre>");
      return "";
    },

    collectionBrief() {
      return this.collectionForm.collection_brief || null;
    },
    hasCollectionBrief() {
      const e = this.collectionBrief;
      return !!(e && (e.body_markdown || e.body_html || e.body));
    },
    collectionBriefWordCount() {
      const e = this.collectionBrief;
      if (!e) return 0;
      const text = e.body_markdown || e.body || "";
      return text ? text.split(/\s+/).filter(Boolean).length : 0;
    },
    collectionBriefHtml() {
      const e = this.collectionBrief;
      if (!e) return "";
      if (e.body_html) return this.wrapHtml(e.body_html);
      if (e.body_markdown || e.body) return this.wrapHtml("<pre style='white-space:pre-wrap;font-family:inherit;'>" + this.esc(e.body_markdown || e.body) + "</pre>");
      return "";
    },

    hasEditorialCover() {
      return !!(this.collectionForm.editorial_cover_html);
    },
  },
  methods: {
    esc(s) {
      const d = document.createElement("div");
      d.textContent = s;
      return d.innerHTML;
    },
    wrapHtml(html) {
      return '<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;font-size:15px;line-height:1.55;color:#1a1a1a;margin:14px;}p{margin:0 0 14px}h1,h2,h3{margin:18px 0 10px}blockquote{margin:14px 0;padding:8px 14px;border-left:3px solid #888;color:#555;font-style:italic;}a{color:#0a6cdf}pre{white-space:pre-wrap;font-family:inherit;}</style></head><body>' + html + '</body></html>';
    },
    copy(text, label) {
      if (!text) return;
      const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) {}
        document.body.removeChild(ta);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(`${label} copied`))
          .catch(() => { fallback(); this.$message.success(`${label} copied`); });
      } else {
        fallback();
        this.$message.success(`${label} copied`);
      }
    },
  },
};
</script>

<style scoped>
.ee-panel { padding: 10px 4px; }

.ee-block { margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #f0f2f5; }
.ee-block:last-child { border-bottom: none; margin-bottom: 0; }

.ee-block-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.ee-block-title { font-weight: 600; font-size: 14px; color: #303133; }
.ee-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 12px; }
.ee-badge.is-set { background: #f0f9eb; color: #67c23a; }
.ee-badge.is-empty { background: #f4f4f5; color: #909399; }
.ee-word-count { font-size: 12px; color: #909399; }

.ee-body-preview { width: 100%; height: 360px; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; background: #fff; }
.ee-body-preview-tall { height: 500px; }
.ee-body-preview iframe { width: 100%; height: 100%; border: 0; }

.ee-cover-preview { width: 100%; max-width: 500px; height: 280px; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; background: #fff; }
.ee-cover-preview iframe { width: 100%; height: 100%; border: 0; }

.ee-actions { display: flex; gap: 8px; padding-top: 8px; }
</style>
